import { __assign, __read } from "tslib";
import * as React from 'react';
var _ReactTreeContext = React.createContext({
    nodes: [],
    selectedNodes: [],
    openNodes: [],
    controlledOpen: false,
    controlledSelected: false,
    options: {
        folderAnimations: false,
        indicatorAnimations: false,
        showEmptyItems: false,
        noIcons: false,
        lazy: false,
        truncateLongText: false,
        multiSelect: false,
        messages: {
            loading: 'Loading...',
            noData: 'No data to render 😔',
            emptyItems: '[Empty]'
        }
    },
    theme: 'light',
    getOpenNodes: function () { return []; },
    getSelectedNodes: function () { return []; },
    toggleNodeSelectedState: function () { },
    toggleNodeOpenState: function () { },
    toggleAllNodesOpenState: function () { },
    toggleAllNodesSelectedState: function () { },
    toggleOpenNodes: function () { },
    toggleClosedNodes: function () { },
    toggleOpenClosedNodes: function () { },
    selectNodes: function () { },
    deselectNodes: function () { },
    toggleSelectedNodes: function () { },
    onToggleOpenNodes: function () { },
    onToggleSelectedNodes: function () { }
});
var ReactTreeContextProvider = function (_a) {
    var _b, _c, _d;
    var children = _a.children, _e = _a.nodes, nodes = _e === void 0 ? [] : _e, defaultSelectedNodes = _a.defaultSelectedNodes, defaultOpenNodes = _a.defaultOpenNodes, selectedNodes = _a.selectedNodes, openNodes = _a.openNodes, _f = _a.options, options = _f === void 0 ? {
        folderAnimations: false,
        indicatorAnimations: false,
        lazy: false,
        showEmptyItems: false,
        noIcons: false,
        truncateLongText: false,
        messages: {
            loading: 'Loading...',
            noData: 'No data to render 😔',
            emptyItems: '[Empty]'
        }
    } : _f, _g = _a.theme, theme = _g === void 0 ? 'light' : _g, apiRef = _a.apiRef, _h = _a.onToggleOpenNodes, onToggleOpenNodes = _h === void 0 ? function () { } : _h, _j = _a.onToggleSelectedNodes, onToggleSelectedNodes = _j === void 0 ? function () { } : _j;
    var _k = __read(React.useState(nodes), 2), nodeList = _k[0], setTreeNodeList = _k[1];
    var _l = __read(React.useState(defaultSelectedNodes || selectedNodes || []), 2), _selectedNodes = _l[0], setSelectedNodes = _l[1];
    var _m = __read(React.useState(defaultOpenNodes || openNodes || []), 2), _openNodes = _m[0], setOpenNodes = _m[1];
    var _o = __read(React.useState(function () {
        if (defaultOpenNodes)
            return false;
        if (openNodes)
            return true;
        return false;
    }), 1), controlledOpen = _o[0];
    var _p = __read(React.useState(function () {
        if (defaultSelectedNodes)
            return false;
        if (selectedNodes)
            return true;
        return false;
    }), 1), controlledSelected = _p[0];
    var _q = __read(React.useState({
        folderAnimations: (options === null || options === void 0 ? void 0 : options.folderAnimations) || false,
        indicatorAnimations: (options === null || options === void 0 ? void 0 : options.indicatorAnimations) || false,
        lazy: options.lazy || false,
        showEmptyItems: options.showEmptyItems || false,
        noIcons: options.noIcons || false,
        truncateLongText: options.truncateLongText || false,
        multiSelect: options.multiSelect || false,
        messages: {
            loading: ((_b = options.messages) === null || _b === void 0 ? void 0 : _b.loading) || 'Loading...',
            noData: ((_c = options.messages) === null || _c === void 0 ? void 0 : _c.noData) || 'No data to render 😔',
            emptyItems: ((_d = options.messages) === null || _d === void 0 ? void 0 : _d.emptyItems) || '[Empty]'
        }
    }), 2), treeConfig = _q[0], setTreeConfig = _q[1];
    // define API methods
    var treeApi = React.useMemo(function () {
        return {
            getOpenNodes: function () { return _openNodes; },
            getSelectedNodes: function () { return _selectedNodes; },
            toggleNodeSelectedState: function (nodeId) {
                setSelectedNodes(function (nodes) {
                    var _nodes = nodes.slice();
                    var i = _nodes.indexOf(nodeId);
                    if (i !== -1) {
                        _nodes.splice(i, 1);
                    }
                    else {
                        _nodes.push(nodeId);
                    }
                    return _nodes;
                });
            },
            toggleNodeOpenState: function (nodeId) {
                setOpenNodes(function (nodes) {
                    var _nodes = nodes.slice();
                    var i = _nodes.indexOf(nodeId);
                    if (i !== -1) {
                        _nodes.splice(i, 1);
                    }
                    else {
                        _nodes.push(nodeId);
                    }
                    return _nodes;
                });
            },
            toggleAllNodesOpenState: function (state) {
                var list = state === 'open' ? nodeList.map(function (n) { return n.id; }) : [];
                setOpenNodes(list);
            },
            toggleAllNodesSelectedState: function (state) {
                var list = state === 'selected'
                    ? nodeList
                        .map(function (n) { return n.id; })
                        .concat(nodeList.flatMap(function (n) { return n.items || []; }).map(function (n) { return n.id; }))
                    : [];
                setSelectedNodes(list);
            },
            toggleOpenNodes: function (nodes) {
                setOpenNodes(function (nList) {
                    return Array.from(new Set(nList.concat(nodes)));
                });
            },
            toggleClosedNodes: function (nodes) {
                setOpenNodes(function (nList) {
                    return nList.filter(function (n) { return !nodes.includes(n); });
                });
            },
            toggleOpenClosedNodes: function (nodes) {
                setOpenNodes(nodes);
            },
            selectNodes: function (nodes) {
                setSelectedNodes(function (nList) {
                    return Array.from(new Set(nList.concat(nodes)));
                });
            },
            deselectNodes: function (nodes) {
                setSelectedNodes(function (nList) {
                    return nList.filter(function (n) { return !nodes.includes(n); });
                });
            },
            toggleSelectedNodes: function (nodes) {
                setSelectedNodes(nodes);
            }
        };
    }, [nodeList, _openNodes, _selectedNodes]);
    // provide external access to API
    React.useImperativeHandle(apiRef, function () { return treeApi; });
    React.useEffect(function () {
        setTreeNodeList(nodes);
    }, [nodes]);
    // handle controlled component states
    React.useEffect(function () {
        if (!openNodes)
            return;
        setOpenNodes(openNodes);
    }, [openNodes]);
    React.useEffect(function () {
        if (!selectedNodes)
            return;
        setSelectedNodes(selectedNodes);
    }, [selectedNodes]);
    // handle event listeners
    React.useEffect(function () {
        onToggleOpenNodes(_openNodes);
    }, [_openNodes]);
    React.useEffect(function () {
        onToggleSelectedNodes(_selectedNodes);
    }, [_selectedNodes]);
    React.useEffect(function () {
        var _a, _b, _c;
        setTreeConfig({
            folderAnimations: (options === null || options === void 0 ? void 0 : options.folderAnimations) || false,
            indicatorAnimations: (options === null || options === void 0 ? void 0 : options.indicatorAnimations) || false,
            lazy: options.lazy || false,
            showEmptyItems: options.showEmptyItems || false,
            noIcons: options.noIcons || false,
            truncateLongText: options.truncateLongText || false,
            multiSelect: options.multiSelect || false,
            messages: {
                loading: ((_a = options.messages) === null || _a === void 0 ? void 0 : _a.loading) || 'Loading...',
                noData: ((_b = options.messages) === null || _b === void 0 ? void 0 : _b.noData) || 'No data to render 😔',
                emptyItems: ((_c = options.messages) === null || _c === void 0 ? void 0 : _c.emptyItems) || '[Empty]'
            }
        });
    }, [options]);
    var value = React.useMemo(function () {
        return __assign(__assign({ nodes: nodeList, selectedNodes: _selectedNodes, openNodes: _openNodes, controlledOpen: controlledOpen, controlledSelected: controlledSelected, options: treeConfig, theme: theme }, treeApi), { onToggleOpenNodes: onToggleOpenNodes, onToggleSelectedNodes: onToggleSelectedNodes });
    }, [
        nodes,
        _selectedNodes,
        _openNodes,
        controlledOpen,
        controlledSelected,
        theme,
        treeApi,
        onToggleOpenNodes,
        onToggleSelectedNodes
    ]);
    return (React.createElement(_ReactTreeContext.Provider, { value: value }, children));
};
var useReactTreeContext = function () { return React.useContext(_ReactTreeContext); };
var ReactTreeContext = {
    Provider: ReactTreeContextProvider,
    Consumer: _ReactTreeContext.Consumer,
    useReactTreeContext: useReactTreeContext
};
export default ReactTreeContext;
//# sourceMappingURL=Context.js.map